import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ButtonCustom from "../../Button";
import "../../../../styles/components/signModal.scss";

interface SignModalProps {
  show: boolean;
  onClose: () => void;
  onSignComplete: (signature: string) => void;
}

const SignModal: React.FC<SignModalProps> = ({
  show,
  onClose,
  onSignComplete,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { t } = useTranslation();
  const [isDrawing, setIsDrawing] = useState(false);

  const handleClear = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const signatureDataUrl = canvas.toDataURL("image/png");
      onSignComplete(signatureDataUrl);
      onClose();
    }
  };

  // Helper function to get exact canvas coordinates accounting for zoom and scaling
  const getCanvasCoordinates = (
    e: React.MouseEvent | React.TouchEvent
  ): { x: number; y: number } | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Get client coordinates (works for both mouse and touch events)
    const clientX =
      "touches" in e && e.touches.length > 0
        ? e.touches[0].clientX
        : "clientX" in e
        ? e.clientX
        : 0;
    const clientY =
      "touches" in e && e.touches.length > 0
        ? e.touches[0].clientY
        : "clientY" in e
        ? e.clientY
        : 0;

    // Calculate exact position accounting for canvas scaling and browser zoom
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;

    return { x, y };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        ctx.strokeStyle = "black"; // Drawing color
      }
    }
  }, []);

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const coords = getCanvasCoordinates(e);
        if (coords) {
          ctx.beginPath();
          ctx.moveTo(coords.x, coords.y);
          setIsDrawing(true);
        }
      }
    }
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const coords = getCanvasCoordinates(e);
        if (coords) {
          ctx.lineTo(coords.x, coords.y);
          ctx.stroke();
        }
      }
    }
  };

  const stopDrawing = (e?: React.MouseEvent | React.TouchEvent) => {
    if (e) {
      e.preventDefault();
    }
    setIsDrawing(false);
  };

  return (
    <Modal show={show} onHide={onClose} centered className="custom-sign-modal">
      <Modal.Header closeButton className="custom-modal-header">
        <Modal.Title>Sign Document</Modal.Title>
      </Modal.Header>
      <Modal.Body className="custom-modal-body">
        <canvas
          ref={canvasRef}
          width={425}
          height={150}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{ touchAction: "none" }}
        />
      </Modal.Body>
      <Modal.Footer className="custom-modal-footer">
        <ButtonCustom
          type="button"
          onClick={handleClear}
          className="btn-primary btn-lg"
          context={t("general.buttons.clear")}
        />
        <ButtonCustom
          type="button"
          onClick={handleSave}
          className="btn-primary btn-lg"
          context={t("general.buttons.save")}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default SignModal;
